/**
 * AdminPortalHeader: the brief at the top of the /admin portal (mockup 01's
 * brief row): the action-first verdict, its lede and the actions row
 * (BriefHero, an h1), the provenance line, and the "Open items" tile beside
 * it (AdminQueueScoreBox), stacking under the brief below 1056px.
 *
 * Copy comes only from `composeAdminBrief()` through BriefHero, which reads
 * the brief's action-first fields (blueprint §4.7); this component never
 * reads them itself. No page title: the breadcrumb names the page and the
 * h1 is the verdict.
 */
import type { ReactNode } from 'react'

import type { AdminBrief } from '@/lib/admin-brief'
import type { ActionBrief } from '@/lib/brief-shape'
import { BriefHero } from '@/components/carbon/BriefHero'
import AdminQueueScoreBox from './AdminQueueScoreBox'

interface AdminPortalHeaderProps {
  /** Pre-composed brief from `composeAdminBrief()` (or a page-level brief while a queue is unknown). */
  brief: AdminBrief | ActionBrief
  /**
   * Sum of the three queue counts: the big figure on the tile. Null while
   * any queue is unknown (not loaded or failed), so the tile shows a dash.
   */
  totalPending: number | null
  /** While the queues load: the brief's announced placeholder and a dashed tile. */
  isLoading?: boolean
  /** Per-queue counts for the tile's bars; null while any queue is unknown. */
  breakdown?: { orgs: number; people: number; tenants: number } | null
  /** The tertiary beside the brief's actions ("Review the pending organizations"). */
  secondaryAction?: ReactNode
  /** The provenance line ("Counted by Guardian at 14:02 · Platform scope"). */
  provenance?: ReactNode
}

export default function AdminPortalHeader({
  brief,
  totalPending,
  isLoading,
  breakdown = null,
  secondaryAction,
  provenance,
}: AdminPortalHeaderProps) {
  return (
    <section
      aria-labelledby="admin-verdict"
      className="mt-8 grid grid-cols-1 items-start gap-8 c-lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]"
    >
      <BriefHero
        brief={brief}
        size="pillar"
        headingId="admin-verdict"
        isLoading={isLoading}
        secondaryAction={secondaryAction}
        provenance={isLoading ? undefined : provenance}
        className="[overflow-wrap:anywhere]"
      />
      <AdminQueueScoreBox
        totalPending={totalPending}
        breakdown={breakdown}
        isLoading={isLoading}
        className="max-c-lg:max-w-[640px]"
      />
    </section>
  )
}
